// src/features/goal/hooks/useGoalDetail.ts
// GoalDetail 頁面的業務邏輯 hook

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useObjectives } from '../contexts/ObjectiveContext';
import { useAuth } from '../../../contexts/AuthContext';
import { useToast } from '../../../contexts/ToastContext';
import { taskService } from '../../task';
import { Task, TaskStatus, OptimisticTask } from '../../../types/task';
import { logger } from '../../../utils/logger';

export function useGoalDetail(objectiveId: string | undefined) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { objectives, loading: objectivesLoading } = useObjectives();
  const { showError } = useToast();

  // 找到對應的 Goal
  const objective = useMemo(() => {
    return objectives.find(obj => obj.id === objectiveId) ?? null;
  }, [objectives, objectiveId]);

  // Task 相關狀態
  const [tasks, setTasks] = useState<(Task | OptimisticTask)[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);

  // GoalBreakdownPanel 顯示狀態
  const [showBreakdownPanel, setShowBreakdownPanel] = useState(false);
  const breakdownPanelRef = useRef<HTMLDivElement>(null);

  // 載入 Tasks
  const loadTasks = useCallback(async () => {
    if (!objectiveId || !currentUser) return;

    try {
      setTasksLoading(true);
      const fetchedTasks = await taskService.getTasksByObjectiveId(objectiveId, currentUser.uid);
      setTasks(fetchedTasks);
    } catch (err) {
      logger.error('GoalDetail: Failed to load tasks', err);
    } finally {
      setTasksLoading(false);
    }
  }, [objectiveId, currentUser]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  // Goal 不存在時導回列表頁
  useEffect(() => {
    if (!objectivesLoading && !objective && objectiveId) {
      showError('Goal not found');
      navigate('/goals', { replace: true });
    }
  }, [objectivesLoading, objective, objectiveId, navigate, showError]);

  // 切換 Task 狀態（樂觀更新）
  const handleToggleTaskStatus = useCallback(async (task: Task) => {
    if (!currentUser) return;

    const newStatus: TaskStatus = task.status === 'OPEN' ? 'CLOSED' : 'OPEN';
    const closeReason = newStatus === 'CLOSED' ? 'completed' as const : undefined;

    // 樂觀更新
    setTasks(prev => prev.map(t =>
      t.id === task.id
        ? { ...t, status: newStatus, closedAt: newStatus === 'CLOSED' ? new Date() : undefined }
        : t
    ));

    try {
      await taskService.updateTaskStatus(task.id, newStatus, currentUser.uid, closeReason);
    } catch (err) {
      // 回滾
      setTasks(prev => prev.map(t =>
        t.id === task.id ? { ...t, status: task.status, closedAt: task.closedAt } : t
      ));
      logger.error('GoalDetail: Failed to toggle task status', err);
    }
  }, [currentUser]);

  // 樂觀加入新 task（由 GoalBreakdownPanel 觸發）
  const handleTaskAdded = useCallback((task: OptimisticTask) => {
    setTasks(prev => [task, ...prev]);
  }, []);

  // 移除 optimistic task（GoalBreakdownPanel API 失敗回滾時觸發）
  const handleTaskAddFailed = useCallback((optimisticId: string) => {
    setTasks(prev => prev.filter(t => t.id !== optimisticId));
  }, []);

  // 用真實 task 替換 optimistic task（GoalBreakdownPanel 寫入成功時觸發）
  const handleTaskCreated = useCallback((optimisticId: string, createdTask: Task) => {
    setTasks(prev => prev.map(t => t.id === optimisticId ? createdTask : t));
  }, []);

  // 手動新增 task（由 GoalTaskList inline input 觸發）
  const handleAddTask = useCallback(async (title: string) => {
    if (!objectiveId || !currentUser) return;

    const optimisticTask: OptimisticTask = {
      id: `optimistic-${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
      title,
      status: 'OPEN',
      priority: '2',
      userId: currentUser.uid,
      objectiveId,
      createdAt: new Date(),
      updatedAt: new Date(),
      migrationCount: 0,
    };

    setTasks(prev => [...prev, optimisticTask]);

    try {
      const createdTask = await taskService.createTask({
        title,
        objectiveId,
        userId: currentUser.uid,
      });
      setTasks(prev => prev.map(t => t.id === optimisticTask.id ? createdTask : t));
    } catch (err) {
      logger.error('GoalDetail: Failed to create task', err);
      setTasks(prev => prev.filter(t => t.id !== optimisticTask.id));
      showError('Failed to add task. Please try again.');
    }
  }, [objectiveId, currentUser, showError]);

  // 開啟 AI 拆解面板並滾動到可見
  const openBreakdownPanel = useCallback(() => {
    setShowBreakdownPanel(true);
    setTimeout(() => {
      breakdownPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 0);
  }, []);

  return {
    objective,
    objectivesLoading,
    currentUser,
    tasks,
    tasksLoading,
    showBreakdownPanel,
    breakdownPanelRef,
    loadTasks,
    handleToggleTaskStatus,
    handleTaskAdded,
    handleTaskAddFailed,
    handleTaskCreated,
    handleAddTask,
    openBreakdownPanel,
    dismissBreakdownPanel: useCallback(() => setShowBreakdownPanel(false), []),
  };
}
